import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  IconButton,
  Stack,
  Typography,
  useTheme,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import Maskgroup1 from "../../assets/Maskgroup.png"; // Correct image import
import Maskgroup2 from "../../assets/Maskgroup2.png";
import Maskgroup3 from "../../assets/Maskgroup3.png";
import Maskgroup4 from "../../assets/Maskgroup4.png";
import Maskgroup5 from "../../assets/Maskgroup5.png";
import Maskgroup6 from "../../assets/Maskgroup6.png";
import Maskgroup7 from "../../assets/Maskgroup7.png";
import Maskgroup8 from "../../assets/Maskgroup8.png";
import Maskgroup9 from "../../assets/Maskgroup9.png";
import Maskgroup10 from "../../assets/Maskgroup10.png";
import Maskgroup11 from "../../assets/Maskgroup11.png";
import Maskgroup12 from "../../assets/Maskgroup12.png";

const brandTheme = createTheme({
  palette: {
    primary: { main: "#2C2FBD" },
    secondary: { main: "#F7931A" },
    background: { default: "#0f1250", paper: "#ffffff" },
    text: { primary: "#fff", secondary: "rgba(255,255,255,0.85)" },
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily:
      'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji"',
    h2: { fontWeight: 800, letterSpacing: -0.5 },
    h4: { fontWeight: 800, letterSpacing: -0.2 },
    body2: { lineHeight: 1.55 },
  },
});

const TESTIMONIALS = [
  {
    title: "Roadside Assistance Company ",
    body: "We get calls round the clock face breakdowns, emergencies, everything. Justtawk team handles it all so smoothly and our customers get help instantly. Feels like having an in-house team, without the extra investment.",
    img: Maskgroup1, // Correct image usage
  },
  {
    title: "Dropshipping Brand ",
    body: "We don’t even hold stock like big E-commerce brands, but our customers think we’re a full-fledged store. From order confirmation to delivery updates, Justtawk and their experts handles everything we just watch the dashboard and work on running ads.",
    img: Maskgroup5,
  },
  {
    title: "Immigration Consultant ",
    body: "We get inquiries from all over the world. With Justtawk, we now speak more than 10 languages. That cultural mix really helps people feel understood and comfortable.",
    img: Maskgroup3,
  },
  {
    title: "Travel & Adventure Agency ",
    body: "During holidays, our phones used to explode with queries. Now, this team 24/7 team takes care of everything, no chaos, no missed calls, just smooth operations.",
    img: Maskgroup7,
  },
  {
    title: "Marketing Agency ",
    body: "We call Justtawk our ‘silent team’. They handle our client calls and leads while we focus on campaigns. Honestly, they make us look more organized than we actually are. Following up with clients are way more easier now and we can focus on what we do best!",
    img: Maskgroup8,
  },
  {
    title: "Finance Consultancy ",
    body: "We run Meta ads daily, and Justtawk agents jump on new leads the moment they come in. No waiting, no follow-up hassle, just fast conversions.",
    img: Maskgroup9,
  },
  {
    title: "Healthcare Provider ",
    body: "In healthcare, empathy is everything. Justtawk’s human agents handle patient calls with real care — way better than any AI-only tool, and still affordable.",
    img: Maskgroup10,
  },
  {
    title: "Real Estate Agency ",
    body: "In real estate, response time is everything. With Justtawk, every inquiry is answered within seconds, they gather the initial info, and then qualified leads are passed on to the key account managers in house. We’ve seen a clear jump in lead conversions and client trust.",
    img: Maskgroup8,
  },
  {
    title: "Trading Company ",
    body: "We’re a growing business, and Justtawk’s support team fits right in — reliable, polite, and totally professional. Great quality service at a price that makes sense.",
    img: Maskgroup11,
  },
];

function scrollByCard(viewport, direction = 1) {
  if (!viewport) return;
  const card = viewport.querySelector("[data-card='true']");
  if (!card) return;
  const step = card.offsetWidth + 24; // includes gap
  viewport.scrollBy({ left: step * direction, behavior: "smooth" });
}

function ProgressBar({ value }) {
  return (
    <Box
      aria-label="Carousel progress"
      role="progressbar"
      aria-valuenow={Math.round(value * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      sx={{
        width: { xs: "60%", sm: 420 },
        height: 6,
        borderRadius: 999,
        bgcolor: "rgba(255,255,255,0.28)",
        mx: "auto",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: `${Math.min(100, Math.max(0, value * 100))}%`,
          bgcolor: "secondary.main",
          borderRadius: 999,
          transition: "width 300ms ease",
        }}
      />
    </Box>
  );
}

export default function TrustedBySlider() {
  const theme = useTheme();
  const viewportRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      const pct = max > 0 ? el.scrollLeft / max : 0;
      setProgress(pct);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const id = setInterval(() => {
      const atEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        scrollByCard(el, +1);
      }
    }, 4500);
    return () => clearInterval(id);
  }, []);

  const slides = useMemo(() => TESTIMONIALS, []);

  return (
    <ThemeProvider theme={brandTheme}>
      <Box
        component="section"
        sx={{
          py: { xs: 6, sm: 8, md: 10 },
          color: "text.primary",
          background:
            "linear-gradient(135deg, #3a3fd2 0%, #5b39ce 30%, #7b39c7 70%, #8c38c2 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Stack
            spacing={2}
            alignItems="center"
            textAlign="center"
            mb={{ xs: 4, md: 6 }}
          >
            <Typography
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                fontSize: { xs: "24px", sm: "28px", md: "32px", lg: "56px" },
                lineHeight: 1.2,
              }}
            >
              Trusted by
            </Typography>

            <Typography
              sx={{
                color: "secondary.main",
                fontWeight: 700,
                letterSpacing: 0.2,
                fontSize: { xs: "28px", sm: "32px", md: "36px", lg: "50px" },
                lineHeight: 0.8,
              }}
            >
              UAE&apos;s Brands & StartUps
            </Typography>
          </Stack>

          <Box sx={{ position: "relative", px: { xs: 1, sm: 2 } }}>
            {/* Arrow controls */}
            <IconButton
              aria-label="Previous testimonials"
              onClick={() => scrollByCard(viewportRef.current, -1)}
              sx={{
                position: "absolute",
                zIndex: 2,
                top: "50%",
                left: -4,
                transform: "translateY(-50%)",
                bgcolor: "rgba(255,255,255,0.25)",
                color: "#fff",
                backdropFilter: "blur(4px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                display: { xs: "none", sm: "flex" },
              }}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              aria-label="Next testimonials"
              onClick={() => scrollByCard(viewportRef.current, +1)}
              sx={{
                position: "absolute",
                zIndex: 2,
                top: "50%",
                right: -4,
                transform: "translateY(-50%)",
                bgcolor: "rgba(255,255,255,0.25)",
                color: "#fff",
                backdropFilter: "blur(4px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                display: { xs: "none", sm: "flex" },
              }}
            >
              <ChevronRightRoundedIcon />
            </IconButton>

            {/* Viewport */}
            <Box
              ref={viewportRef}
              tabIndex={0}
              aria-label="Testimonials carousel"
              sx={{
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                display: "flex",
                gap: 3,
                px: { xs: 0.5, sm: 1 },
                pb: 1,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {slides.map((item, idx) => (
                <Card
                  key={idx}
                  data-card="true"
                  sx={{
                    scrollSnapAlign: "center",
                    flex: "0 0 auto",
                    width: { xs: 260, sm: 300, md: 320 },
                    height: 440,
                    borderRadius: 1.5,
                    boxShadow:
                      "0 8px 18px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(254, 254, 254, 1)",
                    backgroundImage: `url(${item.img})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    color: "#4b40d5",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(188, 190, 246, 0.65) 100%)",
                      zIndex: 1,
                    }}
                  />

                  <CardContent
                    sx={{ p: 2.25, position: "relative", zIndex: 2 }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 1.25,
                        lineHeight: 1.2,
                        p: { xs: "0 4px", sm: "10px 8px" },
                        mt: 2,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.5,
                        p: { xs: "0 4px", sm: "10px 8px" },
                        color: "black",
                      }}
                    >
                      {item.body}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            <Box mt={3}>
              <ProgressBar value={progress} />
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
